// filepath: apps/web/src/features/proc/InterventionControl.tsx
//
// FR-BPM-18 — short authorized control surface for the six FR-BPM-18
// intervention actions. Mounts in FailedInstanceDashboard (and may
// be mounted for any non-terminal instance).
//
// Reuses the design-system Dialog family + the FR-UI-CORE-10 5-state
// renderer (loading / success / submit-failure / stale-version /
// unsupported_domain_action). No mandatory pre-action explanation
// (per AC-10).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import {
  useInterventionControl,
  type InterventionActionKind,
  type InterventionOutcome,
} from "./useInterventionControl";

export interface InterventionControlProps {
  instanceId: string;
  /** Available actions for this instance. The dashboard restricts by
   * instance.status + authorization. */
  availableActions?: InterventionActionKind[];
}

const DEFAULT_ACTIONS: InterventionActionKind[] = [
  "skip",
  "continue_from",
  "repeat",
  "change_remaining_work",
  "finish",
  "cancel",
];

export function InterventionControl({
  instanceId,
  availableActions = DEFAULT_ACTIONS,
}: InterventionControlProps): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<InterventionActionKind | null>(null);
  const [scope, setScope] = useState<string>("instance");
  const [outcome, setOutcome] = useState<InterventionOutcome | null>(null);

  const { mutateAsync, isPending, error } = useInterventionControl({
    onSuccess: (o) => setOutcome(o),
  });

  function close(): void {
    setOpen(false);
    setSelectedAction(null);
    setOutcome(null);
  }

  async function applyAction(action: InterventionActionKind): Promise<void> {
    setSelectedAction(action);
    try {
      await mutateAsync({ instanceId, action, scope });
    } catch {
      // surfaced via the `error` state below
    }
  }

  return (
    <section
      className="intervention-control"
      data-testid="intervention-control"
      aria-labelledby="intervention-control-title"
    >
      <h3
        id="intervention-control-title"
        className="intervention-control-title"
      >
        {t("intervention.title", "Authorized intervention")}
      </h3>
      <button
        type="button"
        className="intervention-control-open"
        data-testid="intervention-control-open"
        onClick={() => setOpen(true)}
      >
        {t("intervention.open", "Intervene…")}
      </button>
      {open && (
        <div
          className="intervention-control-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={t("intervention.dialog_aria", "Intervention dialog")}
        >
          <fieldset>
            <label>
              {t("intervention.scope_label", "Scope")}
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                data-testid="intervention-control-scope"
              >
                <option value="instance">
                  {t("intervention.scope_instance", "Instance")}
                </option>
                <option value="step">
                  {t("intervention.scope_step", "Step")}
                </option>
                <option value="task">
                  {t("intervention.scope_task", "Task")}
                </option>
                <option value="branch">
                  {t("intervention.scope_branch", "Branch")}
                </option>
                <option value="work_package">
                  {t("intervention.scope_work_package", "Work package")}
                </option>
              </select>
            </label>
          </fieldset>
          <ul className="intervention-control-action-list">
            {availableActions.map((a) => (
              <li key={a}>
                <button
                  type="button"
                  className="intervention-control-action"
                  data-testid={`intervention-control-action-${a}`}
                  disabled={isPending}
                  aria-busy={isPending && selectedAction === a}
                  onClick={() => void applyAction(a)}
                >
                  {t(`intervention.action.${a}`, a)}
                </button>
              </li>
            ))}
          </ul>
          {outcome && (
            <div
              className="intervention-control-outcome"
              data-testid="intervention-control-outcome"
              data-disposition={outcome.disposition}
            >
              <p>
                {t("intervention.outcome.applied", "Intervention applied:")}{" "}
                <strong>{outcome.disposition}</strong>
              </p>
              <p className="intervention-control-outcome-detail">
                {t("intervention.outcome.audit", "Audit ID:")}{" "}
                {outcome.audit_id}
              </p>
            </div>
          )}
          {error && (
            <p
              role="alert"
              className="intervention-control-error"
              data-testid="intervention-control-error"
              data-error-code={error.code}
            >
              {t(`intervention.error.${error.code}`, error.message)}
            </p>
          )}
          <button
            type="button"
            onClick={close}
            className="intervention-control-close"
            data-testid="intervention-control-close"
          >
            {t("common.close", "Close")}
          </button>
        </div>
      )}
    </section>
  );
}
